/**
 * CreatureRosterDisplay Component
 * L4-EGE-005: Display both teams' creatures in parallel columns
 *
 * Shows "YOUR TEAM" on left, "OPPONENT TEAM" on right.
 * Creatures are sorted by damage dealt (highest first).
 */

import type { CreaturePerformance } from '../../utils/combat-stats';
import type { PlayerId } from '@drawn-of-war/shared';
import { CreatureCard } from './CreatureCard';
import styles from './CreatureRosterDisplay.module.css';

export interface CreatureRosterDisplayProps {
  /** All creature performances from the battle */
  performances: CreaturePerformance[];
  /** Current player's ID (for determining "YOUR TEAM" side) */
  currentPlayerId: PlayerId;
  /** Unit ID of player's MVP (if any) */
  playerMVPUnitId?: string;
  /** Unit ID of opponent's MVP (if any) */
  opponentMVPUnitId?: string;
}

/** Delay between each card reveal animation (ms) */
const STAGGER_DELAY = 100;

/**
 * CreatureRosterDisplay Component
 *
 * Displays both teams' creatures in parallel columns after battle ends.
 */
export function CreatureRosterDisplay({
  performances,
  currentPlayerId,
  playerMVPUnitId,
  opponentMVPUnitId
}: CreatureRosterDisplayProps): JSX.Element {
  const opponentId: PlayerId = currentPlayerId === 'player1' ? 'player2' : 'player1';

  // Split performances by team and sort by damage dealt (descending)
  const playerTeam = performances
    .filter(p => p.ownerId === currentPlayerId)
    .sort((a, b) => b.damageDealt - a.damageDealt);

  const opponentTeam = performances
    .filter(p => p.ownerId === opponentId)
    .sort((a, b) => b.damageDealt - a.damageDealt);

  return (
    <section className={styles.container} aria-labelledby="roster-heading">
      <h3 id="roster-heading" className={styles.heading}>
        CREATURE PERFORMANCE
      </h3>

      <div className={styles.roster}>
        {/* Player's team column */}
        <div className={styles.teamColumn}>
          <h4 className={styles.teamLabel}>YOUR TEAM</h4>
          <div className={styles.creatureList} role="list" aria-label="Your team creatures">
            {playerTeam.map((perf, index) => (
              <CreatureCard
                key={perf.unitId}
                performance={perf}
                isMVP={perf.unitId === playerMVPUnitId}
                delay={index * STAGGER_DELAY}
              />
            ))}
            {playerTeam.length === 0 && (
              <div className={styles.empty}>No creatures</div>
            )}
          </div>
        </div>

        {/* VS divider */}
        <div className={styles.divider} aria-hidden="true">
          <span>VS</span>
        </div>

        {/* Opponent's team column */}
        <div className={styles.teamColumn}>
          <h4 className={styles.teamLabel}>OPPONENT TEAM</h4>
          <div className={styles.creatureList} role="list" aria-label="Opponent team creatures">
            {opponentTeam.map((perf, index) => (
              <CreatureCard
                key={perf.unitId}
                performance={perf}
                isMVP={perf.unitId === opponentMVPUnitId}
                delay={index * STAGGER_DELAY}
              />
            ))}
            {opponentTeam.length === 0 && (
              <div className={styles.empty}>No creatures</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default CreatureRosterDisplay;
